import {
  CalendarIntervalUnit,
  IntervalUnit,
  isCalendarInterval,
} from "src/types/visual";

/**
 * Restrictions a consuming surface can impose on the query builder.
 *
 * Shared query-builder components check for denied features instead of
 * knowing about specific surfaces, so any caller (predictive traits, admin
 * tools, etc.) can compose the set that matches its semantics.
 */
export type FilterFeature =
  /** Trait conditions: the filter section and trait option queries. */
  | "traits"
  /** Audience conditions: the filter section and related-audience queries. */
  | "audiences"
  /** Journey conditions: the filter section and journey queries. */
  | "journeys"
  /** Related-model conditions: the filter section. */
  | "related-models"
  /** Event relationships other than the predictor's key events. */
  | "non-key-events"
  /** Funnel ("then did") conditions on events. */
  | "funnels"
  /** The previous/next/current direction selector on event windows. */
  | "window-directions"
  /** Now-relative date operators and values: within, anniversary, and
   * relative previous/next/current timestamps. */
  | "relative-dates"
  /** Trait comparisons on the right-hand side of conditions. */
  | "trait-comparisons";

export type FilterRestrictions = {
  /** Filter features hidden from the UI entirely. */
  denied?: readonly FilterFeature[];
  /** Max reach-back of relative event windows; enforced by clamping the
   * window quantity/unit inputs rather than post-hoc validation. */
  maxRelativeWindowDays?: number;
};

export const isDenied = (
  restrictions: FilterRestrictions | undefined,
  feature: FilterFeature,
): boolean => restrictions?.denied?.includes(feature) ?? false;

// Mirrors `getIntervalInMs` (src/utils/time.ts), which the query compiler's
// date math is based on.
const DAYS_PER_INTERVAL_UNIT: Partial<Record<IntervalUnit, number>> = {
  [IntervalUnit.Minute]: 1 / (24 * 60),
  [IntervalUnit.Hour]: 1 / 24,
  [IntervalUnit.Day]: 1,
  [IntervalUnit.Week]: 7,
  [IntervalUnit.Month]: 30,
  [IntervalUnit.Year]: 365,
};

const CALENDAR_TO_PLAIN_UNIT: Record<CalendarIntervalUnit, IntervalUnit> = {
  [CalendarIntervalUnit.Day]: IntervalUnit.Day,
  [CalendarIntervalUnit.Month]: IntervalUnit.Month,
  [CalendarIntervalUnit.Year]: IntervalUnit.Year,
};

/**
 * The largest window quantity of `interval` that stays within `maxDays`, or
 * undefined when there is no cap or the unit is unknown. "Previous N
 * calendar <unit>s" also spans the current partial period, reaching back up
 * to N+1 plain units, so calendar units fit one fewer than their plain
 * equivalent. Returns 0 when not even one unit fits, in which case the unit
 * should be hidden.
 */
export const getMaxWindowQuantity = (
  interval: IntervalUnit | CalendarIntervalUnit,
  maxDays: number | undefined,
): number | undefined => {
  if (maxDays === undefined) {
    return undefined;
  }
  const isCalendar = isCalendarInterval(interval);
  const plainUnit = isCalendar ? CALENDAR_TO_PLAIN_UNIT[interval] : interval;
  const daysPerUnit = DAYS_PER_INTERVAL_UNIT[plainUnit];
  if (daysPerUnit === undefined) {
    return undefined;
  }
  const maxPlainUnits = Math.floor(maxDays / daysPerUnit);
  return Math.max(isCalendar ? maxPlainUnits - 1 : maxPlainUnits, 0);
};
